import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { datadogLogs } from '@datadog/browser-logs';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import { CustomTable, tableButton } from 'components/CustomTable';
import { ConfirmationModal } from 'components/Modal/confirmationModal';
import { showToast } from 'components/Toast';
import { recipientAdminColumns } from 'constants/tables';
import { useAppSelector } from 'helpers/hooks';
import { getApiHttpStatus } from 'helpers/rtk-utils';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';
import { has_permission } from 'config/permissions';
import { getActiveRole, getSelectedRecipientId } from 'selectors/admins';
import { useCheckOnboardingStatusQuery } from 'services/adminsApi';
import {
  RecipientAdminRow,
  useArchiveRecipientAdminMutation,
  useGetRecipientAdminsQuery,
  useUnarchiveRecipientAdminMutation,
} from 'services/recipientAdminsApi';
import { AddAdminModal } from './AddAdminModal';
import { AdminDetailModal } from './AdminDetailModal';
import { PrimaryContactCard } from './PrimaryContactCard';
import { COPY } from './constants';
import {
  canArchiveRow,
  canRestoreRow,
  describeRecipientAdminFailure,
  getPositionCategoryLabel,
  getStatusLabel,
  isArchivedRow,
  isTermsAccepted,
} from './helpers';
import { PendingRowAction, RecipientAdminTableRow, RowAction } from './types';

const SCREEN: DashboardScreen = 'settings_recipient_admins';

export const RecipientAdmins = () => {
  const activeRole = useAppSelector(getActiveRole);
  const recipientId = useAppSelector(getSelectedRecipientId);
  const canViewAdmins = has_permission('SETTINGS_RECIPIENT_ADMINS_ROUTE', 'R', activeRole);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<RecipientAdminRow | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingRowAction | null>(null);

  // currentData (not data) so a recipient switch never briefly shows the previous
  // recipient's admin list while the new recipient's request is in flight.
  const {
    currentData: adminsResponse,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetRecipientAdminsQuery({ recipientId }, { skip: !recipientId || !canViewAdmins });

  const isForbidden = isError && (error as FetchBaseQueryError)?.status === 403;

  const { data: OBCheck } = useCheckOnboardingStatusQuery(recipientId, {
    skip: !recipientId || !canViewAdmins,
  });

  const [archiveAdmin] = useArchiveRecipientAdminMutation();
  const [unarchiveAdmin] = useUnarchiveRecipientAdminMutation();

  const handleRowAction = (action: RowAction, adminUserId: string) => {
    const admin = adminsResponse?.admins.find(row => row.admin_user_id === adminUserId);
    if (!admin) return;

    trackEvent('Recipient Admins Row Action Clicked', { screen: SCREEN, action });

    if (action === 'view') {
      setSelectedAdmin(admin);
      return;
    }

    setPendingAction({
      adminUserId,
      adminName: `${admin.first_name} ${admin.last_name}`,
      action,
    });
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    const { adminUserId, action } = pendingAction;
    const isArchiving = action === 'archive';
    try {
      if (isArchiving) {
        await archiveAdmin({ recipientId, adminUserId }).unwrap();
      } else {
        await unarchiveAdmin({ recipientId, adminUserId }).unwrap();
      }
      showToast(`Admin ${isArchiving ? 'archived' : 'restored'} successfully`, 'success');
      trackEvent(isArchiving ? 'Recipient Admin Archived' : 'Recipient Admin Restored', {
        screen: SCREEN,
      });
    } catch (err: unknown) {
      const { message, severity, logMessage, logContext } = describeRecipientAdminFailure(
        err,
        action
      );
      const logDetails = { recipientId, adminUserId, ...logContext };
      if (severity === 'info') {
        datadogLogs.logger.info(logMessage, logDetails);
      } else {
        datadogLogs.logger.error(logMessage, logDetails, err instanceof Error ? err : undefined);
      }
      showToast(message, 'error', getApiHttpStatus(err));
    } finally {
      setPendingAction(null);
    }
  };

  const tableRows: RecipientAdminTableRow[] = useMemo(() => {
    if (!adminsResponse) return [];
    const { admins, active_count, active_admin_limit } = adminsResponse;

    return admins.map(admin => {
      const actions = (
        <div className="grid-column">
          <div className="table-action-row">
            {tableButton(handleRowAction, 'view', admin.admin_user_id)}
            {!isArchivedRow(admin) &&
              tableButton(
                handleRowAction,
                'archive',
                admin.admin_user_id,
                canArchiveRow(admin) ? undefined : COPY.primaryContactArchiveDisabledReason
              )}
            {isArchivedRow(admin) &&
              tableButton(
                handleRowAction,
                'restore',
                admin.admin_user_id,
                canRestoreRow(admin, active_count, active_admin_limit)
                  ? undefined
                  : COPY.atCap(active_count, active_admin_limit)
              )}
          </div>
        </div>
      );

      return {
        id: admin.admin_user_id,
        name: `${admin.first_name} ${admin.last_name}`,
        email: admin.email,
        positionCategory: getPositionCategoryLabel(admin.position_category),
        tosAcceptedLabel: isTermsAccepted(admin) ? 'Yes' : 'No',
        statusLabel: getStatusLabel(admin),
        createdLabel: dayjs(admin.created_at).format('MMM D, YYYY'),
        isPrimaryContact: admin.is_primary_contact,
        actions,
      };
    });
  }, [adminsResponse]);

  if (!canViewAdmins) {
    return (
      <div className="content-full-width recipient-admins-content">
        <h1 className="title-permission">
          Sorry, you don&apos;t have permission to see this screen.
        </h1>
      </div>
    );
  }

  if ((isLoading || isFetching) && !adminsResponse) {
    return <Loader type="dots" />;
  }

  if (isForbidden) {
    return <PrimaryContactCard primaryContact={OBCheck?.primary_contact} />;
  }

  if (isError) {
    return (
      <div className="content-full-width recipient-admins-content">
        <span className="oath-bodysmall">
          Couldn&apos;t load admins.{' '}
          <button type="button" className="link-text inkBlue padding0" onClick={() => refetch()}>
            Retry
          </button>
        </span>
      </div>
    );
  }

  const activeCount = adminsResponse?.active_count ?? 0;
  const archivedCount = adminsResponse?.archived_count ?? 0;
  const activeLimit = adminsResponse?.active_admin_limit ?? 0;
  const atCap = !!adminsResponse && activeCount >= activeLimit;

  return (
    <div className="content-full-width recipient-admins-content">
      <div className="row custom-header-container">
        <div className="grid-column col-lg-9 col-md-8 col-sm-12 align-center">
          <span className="oath-h3 inkBlue">Admins</span>
          <span className="oath-bodysmall inkBlue">
            {activeCount} active{archivedCount > 0 && ` · ${archivedCount} archived`} · Limit:{' '}
            {activeLimit}
          </span>
        </div>
        <div className="new-page-button col-lg-3 col-md-4 col-sm-12">
          <button
            type="button"
            className="btn-inkBlue btn btn-secondary btn-block"
            disabled={atCap}
            onClick={() => {
              trackEvent('Recipient Admins Add Opened', { screen: SCREEN });
              setIsAddOpen(true);
            }}
          >
            Add admin
          </button>
          {atCap && (
            <span className="oath-bodysmall errorRed">{COPY.atCap(activeCount, activeLimit)}</span>
          )}
        </div>
      </div>

      {tableRows.length === 0 ? (
        <span className="oath-bodysmall gray">{COPY.emptyTable}</span>
      ) : (
        <div className="row recent-donations-table">
          <div className="col-lg-12">
            <CustomTable
              key="recipient-admins-list"
              data={tableRows}
              columns={recipientAdminColumns}
              withPagination
              screen={SCREEN}
            />
          </div>
        </div>
      )}

      <AddAdminModal
        isOpen={isAddOpen}
        recipientId={recipientId}
        admins={adminsResponse?.admins ?? []}
        onClose={() => setIsAddOpen(false)}
      />

      <AdminDetailModal admin={selectedAdmin} onClose={() => setSelectedAdmin(null)} />

      <ConfirmationModal
        isOpen={!!pendingAction}
        eyebrowText={pendingAction?.action === 'archive' ? 'Archive admin' : 'Restore admin'}
        warningMessage={
          !pendingAction
            ? ''
            : pendingAction.action === 'archive'
              ? COPY.archiveConfirm(pendingAction.adminName)
              : COPY.restoreConfirm(pendingAction.adminName)
        }
        confirmButtonText={pendingAction?.action === 'archive' ? 'Archive' : 'Restore'}
        handleSave={handleConfirmAction}
        handleCancel={() => setPendingAction(null)}
      />
    </div>
  );
};
